import Image from "next/image";
import dynamic from 'next/dynamic';
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface DirectusInterfaceData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // buttons
  "button.label",
  "button.href",

  // images
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
] as const;

interface NewsletterProps {
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
  data?: DirectusInterfaceData;
}

export default function Newsletter({
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation,
  data
}: NewsletterProps) {
  const imageUrl = data?.image?.image_file?.image?.id && data?.image?.image_file?.image?.filename_download 
    ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download) 
    : "";
  const imageAlt = data?.image?.alt || "";

  console.log("data", data);
  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: "100vh", x: 0, opacity: 1 },
              below: { y: "-100vh", x: 0, opacity: 1 },
            },
            mobile: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: 0, x: "100vw", opacity: 1 },
              below: { y: "-100vh", x: 0, opacity: 1 },
            },
          }}
        >
          <div className="h-full relative w-full bg-white overflow-hidden">
            {/* Container */}
            <div className="relative h-full w-full flex items-center justify-center">
              <div className="w-full max-w-[1440px] mx-auto flex lg:flex-row items-center justify-center">
                {/* Left Side - Pattern */}
                <AnimatedSection
                  className="hidden lg:block lg:w-1/2 relative h-[450px] aspect-square"
                  zIndex={zIndex}
                  thresholds={[
                    thresholds?.[0]?.[0],
                    thresholds?.[thresholds.length - 1]?.[1],
                  ]}
                  transitionTime={transitionTime}
                  variants={{
                    desktop: {
                      visible: { opacity: 1, scale: 1, x: 0 },
                      above: { opacity: 1, scale: 3, x: 0 },
                      below: { opacity: 1, scale: 1, x: 0 },
                    },
                    mobile: {
                      visible: { opacity: 1, scale: 1, x: 0 },
                      above: { opacity: 1, scale: 0, x: 0 },
                      below: { opacity: 1, scale: 1, x: 0 },
                    },
                  }}
                >
                  <Image
                    src={imageUrl}
                    alt={imageAlt}
                    fill
                    className="object-contain"
                    priority
                    quality={90}
                  />
                </AnimatedSection>
                <div>
                  {/* Right Side - Content */}
                  <div className="w-full lg:w-3/4 px-7 lg:px-0 relative z-10">
                    <div className="flex flex-col max-w-[800px] mx-auto lg:mx-0 items-center lg:items-start text-center lg:text-left space-y-6 lg:space-y-9">
                      <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-nowrap" 
                        dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.text || data?.heading?.title || "")}
                      />
                      <div
                        className="text-[1rem] font-normal leading-normal lg:text-[1.25rem] font-greycliff text-primary tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-[1.25rem]"
                        dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                      />
                      <a href={data?.button?.href || "#"} className="w-fit px-20 lg:px-9 py-6 lg:py-3 bg-primary rounded-full hover:bg-secondary transition-colors">
                        <span className="text-xl text-white font-normal font-greycliff leading-7 tracking-tight">
                          {data?.button?.label}
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Pattern - Positioned behind content */}
              <AnimatedSection
                className="absolute top-0 w-full h-full lg:hidden"
                
                mobileThresholds={[
                  mobileThresholds?.[0]?.[0],
                  mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                ]}
                transitionTime={transitionTime}
                variants={{
                  mobile: {
                    visible: { opacity: 1, scale: 1, x: 0 },
                    above: { opacity: 1, scale: 3, x: 0 },
                    below: { opacity: 1, scale: 1, x: 0 },
                  },
                }}
              >
                <Image
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-contain scale-150 left-1/2 opacity-60"
                  priority
                  quality={90}
                />
              </AnimatedSection>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full">
          <div className="h-full relative w-full bg-white overflow-hidden">
            {/* Container */}
            <div className="relative h-full w-full flex items-center justify-center py-8 lg:py-16">
              <div className="w-full max-w-[1440px] mx-auto flex xl:flex-row items-center justify-center">
                {/* Left Side - Pattern */}
                <div className="hidden xl:block xl:w-1/2 relative h-[450px] aspect-square">
                  <Image
                    src={imageUrl}
                    alt={imageAlt}
                    fill
                    className="object-contain"
                    priority
                    quality={90}
                  />
                </div>
                <div>
                  {/* Right Side - Content */}
                  <div className="w-full xl:w-3/4 px-7 xl:px-0 relative z-10">
                    <div className="flex flex-col max-w-[800px] mx-auto xl:mx-0 items-center xl:items-start text-center xl:text-left space-y-6 xl:space-y-9">
                      <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary text-nowrap" 
                        dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.text || data?.heading?.title || "")}
                      />
                      <div
                        className="text-[1rem] font-normal leading-normal xl:text-[1.25rem] font-greycliff text-primary tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal xl:[&>*]:text-[1.25rem]"
                        dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                      />
                      <a href={data?.button?.href || "#"} className="w-fit px-20 xl:px-9 py-6 xl:py-3 bg-primary rounded-full hover:bg-secondary transition-colors">
                        <span className="text-xl text-white font-normal font-greycliff leading-7 tracking-tight">
                          {data?.button?.label}
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Pattern - Positioned behind content */}
              <div className="absolute top-0 w-full h-full xl:hidden">
                <Image
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-contain scale-150 left-1/2 opacity-60"
                  priority
                  quality={90}
                />
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
